import { createFileRoute, Link } from '@tanstack/react-router'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { flattenPages } from '@/api/pagination'
import type { AuditEntryDto } from '@/api/types'
import { auditQuery, type AuditFilters } from '@/features/platform/platform.queries'
import { formatDateTime } from '@/lib/format'
import { Badge, Card, Copyable, DataTable, PageHeader, type Column } from '@/ui'

const searchSchema = z.object({
  actorId: z.string().optional(),
  action: z.string().optional(),
  resourceType: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
})

export const Route = createFileRoute('/_authed/audit/')({
  validateSearch: searchSchema,
  component: AuditPage,
})

function AuditPage() {
  const { t, i18n } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const filters: AuditFilters = {
    actorId: search.actorId,
    action: search.action,
    resourceType: search.resourceType,
    from: search.from,
    to: search.to,
  }

  const query = useInfiniteQuery(auditQuery(filters))
  const { items, total } = flattenPages<AuditEntryDto>(query.data)

  const columns: Column<AuditEntryDto>[] = [
    {
      id: 'when',
      header: t('audit.columnWhen'),
      width: '12rem',
      cell: (entry) => (
        <span className="text-text-secondary">{formatDateTime(entry.createdAt, i18n.language)}</span>
      ),
    },
    {
      id: 'who',
      header: t('audit.columnWho'),
      cell: (entry) => (
        <Link
          to="/users/$userId"
          params={{ userId: entry.actorId }}
          search={{ tab: 'overview' }}
          className="truncate font-medium text-text hover:underline"
        >
          {entry.actorDisplayName}
        </Link>
      ),
    },
    {
      id: 'what',
      header: t('audit.columnWhat'),
      cell: (entry) => (
        <div className="flex min-w-0 items-center gap-2">
          <Badge>{entry.action}</Badge>
          <span className="min-w-0 truncate text-text-secondary">{entry.summary}</span>
        </div>
      ),
    },
    {
      id: 'resource',
      header: t('audit.columnResource'),
      isSecondary: true,
      cell: (entry) => (
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="shrink-0 text-xs text-text-secondary">{entry.resourceType}</span>
          <Copyable value={entry.resourceId} className="min-w-0" />
        </div>
      ),
    },
  ]

  const hasFilters = Boolean(
    search.actorId ?? search.action ?? search.resourceType ?? search.from ?? search.to,
  )

  return (
    <>
      <PageHeader
        title={t('audit.title')}
        description={t('audit.subtitle')}
        actions={
          total > 0 ? (
            <span data-numeric className="text-[0.8125rem] text-text-secondary">
              {t('common.showing', { count: items.length, total })}
            </span>
          ) : undefined
        }
      />

      <Card isFlush>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <label className="text-xs text-text-secondary" htmlFor="audit-from">
            {t('audit.from')}
          </label>
          <input
            id="audit-from"
            type="date"
            value={search.from?.slice(0, 10) ?? ''}
            onChange={(event) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  from: event.target.value ? new Date(event.target.value).toISOString() : undefined,
                }),
              })
            }}
            className="h-9 rounded-control border border-border-strong/60 bg-surface px-2.5 text-[0.8125rem] text-text"
          />

          <label className="text-xs text-text-secondary" htmlFor="audit-to">
            {t('audit.to')}
          </label>
          <input
            id="audit-to"
            type="date"
            value={search.to?.slice(0, 10) ?? ''}
            onChange={(event) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  to: event.target.value ? new Date(event.target.value).toISOString() : undefined,
                }),
              })
            }}
            className="h-9 rounded-control border border-border-strong/60 bg-surface px-2.5 text-[0.8125rem] text-text"
          />
        </div>

        <DataTable
          columns={columns}
          rows={items}
          getRowId={(entry) => entry.id}
          caption={t('audit.title')}
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          emptyTitle={hasFilters ? t('state.emptyFiltered') : t('audit.empty')}
          emptyDescription={hasFilters ? t('state.emptyFilteredHint') : undefined}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => {
            void query.fetchNextPage()
          }}
        />
      </Card>
    </>
  )
}
